import { useState, useEffect } from "react";
import { finance, projects as projectsApi } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, TrendingUp, TrendingDown, IndianRupee } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const formatCurrency = (val) => {
  if (val >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)} L`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
  return val?.toLocaleString("en-IN") || "0";
};

export default function FinanceTracker() {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [projectNames, setProjectNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ project_name: "", type: "income", amount: "", description: "", date: "", category: "" });

  const fetchData = async () => {
    try {
      const [entriesRes, summaryRes, projectsRes] = await Promise.all([
        finance.list({}),
        finance.summary(),
        projectsApi.list()
      ]);
      setEntries(entriesRes.data);
      setSummary(summaryRes.data);
      setProjectNames(projectsRes.data.map((p) => p.name));
    } catch (err) {
      toast.error("Failed to load finance data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await finance.create({ ...form, amount: parseFloat(form.amount) || 0 });
      toast.success("Entry added");
      setDialogOpen(false);
      setForm({ project_name: "", type: "income", amount: "", description: "", date: "", category: "" });
      fetchData();
    } catch (err) {
      toast.error("Failed to add entry");
    }
  };

  const handleDelete = async (id) => {
    try {
      await finance.remove(id);
      toast.success("Entry deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const inputCls = "w-full bg-[#0A0A08] border border-[#27272A] text-[#F5F2E8] px-3 py-2.5 text-sm focus:border-[#FF5C1A] focus:ring-1 focus:ring-[#FF5C1A] outline-none transition-colors placeholder:text-[#444]";

  const chartData = summary?.by_project?.map((p) => ({
    name: p.project_name.length > 20 ? p.project_name.substring(0, 20) + "..." : p.project_name,
    income: p.income,
    expense: p.expense,
    profit: p.profit,
  })) || [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div className="bg-[#111110] border border-[#FF5C1A]/30 p-3 text-xs font-mono">
          <p className="text-[#F5F2E8] mb-1">{label}</p>
          {payload.map((p, i) => (
            <p key={i} style={{ color: p.color }}>{p.name}: {formatCurrency(p.value)}</p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) return <div className="text-center py-12 text-[#888880] font-mono text-sm">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="finance-tracker">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-3xl tracking-[2px]">FINANCE TRACKER</h2>
          <p className="text-sm text-[#888880] mt-1">Income, expenses, and profit analysis</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-finance-btn" className="bg-[#FF5C1A] text-[#0A0A08] px-5 py-2.5 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors flex items-center gap-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.5)] active:translate-y-[2px] active:shadow-none">
              <Plus size={16} /> Add Entry
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#111110] border-[#27272A] text-[#F5F2E8] max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl tracking-[2px]">ADD FINANCE ENTRY</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div>
                <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Project</label>
                <select data-testid="finance-project-select" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} required className={inputCls}>
                  <option value="">Select Project</option>
                  {projectNames.map((n) => <option key={n} value={n}>{n}</option>)}
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Type</label>
                  <select data-testid="finance-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
                    <option value="income">Income</option>
                    <option value="expense">Expense</option>
                  </select>
                </div>
                <div>
                  <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Amount</label>
                  <input data-testid="finance-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required className={inputCls} placeholder="500000" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Date</label>
                  <input data-testid="finance-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Category</label>
                  <select data-testid="finance-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                    <option value="">Select</option>
                    <option value="milestone">Milestone Payment</option>
                    <option value="advance">Advance</option>
                    <option value="material">Material</option>
                    <option value="labour">Labour</option>
                    <option value="transport">Transport</option>
                    <option value="pending">Pending Payment</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Description</label>
                <input data-testid="finance-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} placeholder="Cement purchase..." />
              </div>
              <button data-testid="submit-finance-btn" type="submit" className="w-full bg-[#FF5C1A] text-[#0A0A08] py-3 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors">
                Add Entry
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-[#111110] border border-[#27272A] p-6" data-testid="total-income-card">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={18} className="text-[#2ECC71]" />
            <span className="font-mono text-[10px] text-[#888880] tracking-widest uppercase">Total Income</span>
          </div>
          <div className="font-heading text-4xl text-[#2ECC71]">{formatCurrency(summary?.total_income || 0)}</div>
        </div>
        <div className="bg-[#111110] border border-[#27272A] p-6" data-testid="total-expense-card">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown size={18} className="text-[#E74C3C]" />
            <span className="font-mono text-[10px] text-[#888880] tracking-widest uppercase">Total Expense</span>
          </div>
          <div className="font-heading text-4xl text-[#E74C3C]">{formatCurrency(summary?.total_expense || 0)}</div>
        </div>
        <div className="bg-[#111110] border border-[#27272A] p-6" data-testid="total-profit-card">
          <div className="flex items-center gap-2 mb-3">
            <IndianRupee size={18} className="text-[#FFB800]" />
            <span className="font-mono text-[10px] text-[#888880] tracking-widest uppercase">Net Profit</span>
          </div>
          <div className={`font-heading text-4xl ${(summary?.profit || 0) >= 0 ? 'text-[#FFB800]' : 'text-[#E74C3C]'}`}>
            {formatCurrency(summary?.profit || 0)}
          </div>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-[#111110] border border-[#27272A] p-6" data-testid="finance-chart">
          <h3 className="font-heading text-xl tracking-[2px] mb-6">INCOME VS EXPENSE BY PROJECT</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} barGap={4}>
              <XAxis dataKey="name" tick={{ fill: '#888880', fontSize: 10, fontFamily: 'Space Mono' }} axisLine={{ stroke: '#27272A' }} tickLine={false} />
              <YAxis tick={{ fill: '#888880', fontSize: 10, fontFamily: 'Space Mono' }} axisLine={{ stroke: '#27272A' }} tickLine={false} tickFormatter={(v) => formatCurrency(v)} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="income" fill="#2ECC71" radius={[2, 2, 0, 0]} />
              <Bar dataKey="expense" fill="#E74C3C" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Per-Project Summary */}
      {summary?.by_project?.length > 0 && (
        <div className="bg-[#111110] border border-[#27272A] p-6" data-testid="project-profit-summary">
          <h3 className="font-heading text-xl tracking-[2px] mb-4">PROFIT BY PROJECT</h3>
          <div className="space-y-2">
            {summary.by_project.map((p, i) => (
              <div key={i} className="bg-[#0A0A08] px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="text-sm font-medium truncate">{p.project_name}</div>
                <div className="flex gap-6 text-xs font-mono">
                  <span className="text-[#2ECC71]">+{formatCurrency(p.income)}</span>
                  <span className="text-[#E74C3C]">-{formatCurrency(p.expense)}</span>
                  <span className={p.profit >= 0 ? 'text-[#FFB800]' : 'text-[#E74C3C]'}>=  {formatCurrency(p.profit)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Entries */}
      <div className="bg-[#111110] border border-[#27272A] p-6" data-testid="finance-entries">
        <h3 className="font-heading text-xl tracking-[2px] mb-4">RECENT ENTRIES</h3>
        {entries.length === 0 ? (
          <div className="text-[#888880] text-sm text-center py-6">No entries yet</div>
        ) : (
          <div className="space-y-2">
            {entries.slice(0, 20).map((e) => (
              <div key={e.id} data-testid={`finance-entry-${e.id}`} className="bg-[#0A0A08] px-4 py-3 flex items-center gap-4 group">
                <div className={`w-1 h-8 flex-shrink-0 ${e.type === 'income' ? 'bg-[#2ECC71]' : 'bg-[#E74C3C]'}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{e.description || e.project_name}</div>
                  <div className="flex gap-3 text-xs text-[#888880] mt-0.5">
                    <span>{e.project_name}</span>
                    {e.date && <span className="font-mono">{e.date}</span>}
                    {e.category && <span className="uppercase font-mono text-[10px]">{e.category}</span>}
                  </div>
                </div>
                <div className={`font-mono text-sm font-bold ${e.type === 'income' ? 'text-[#2ECC71]' : 'text-[#E74C3C]'}`}>
                  {e.type === 'income' ? '+' : '-'}{formatCurrency(e.amount)}
                </div>
                <button data-testid={`delete-finance-${e.id}`} onClick={() => handleDelete(e.id)} className="text-[#27272A] hover:text-[#E74C3C] transition-colors opacity-0 group-hover:opacity-100">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
